import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  // Guardamos el ancho exacto de la pantalla
  #lastWindowWidth = window.innerWidth;

  connectedCallback() {
    super.connectedCallback();
    
    // 1. Asegurar que marqueeItems sea un array (corrección vital)
    const marqueeItems = this.#getMarqueeItems();
    if (marqueeItems.length === 0) return;

    // 2. Inicializar
    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    // 3. Listeners
    // Usamos el resize "inteligente"
    window.addEventListener('resize', this.#handleResize);
    
    // Efectos de hover (opcional, si quieres que pare al pasar el mouse)
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  #animation = null;

  // Helper para evitar errores si refs.marqueeItems es un solo elemento
  #getMarqueeItems() {
    const { marqueeItems } = this.refs;
    if (!marqueeItems) return [];
    return Array.isArray(marqueeItems) ? marqueeItems : [marqueeItems];
  }

  // --- CORRECCIÓN CRÍTICA PARA IPHONE ---
  #handleResize = debounce(() => {
    // Si el ancho de la pantalla NO ha cambiado, significa que es 
    // solo el scroll vertical de iOS (barra de direcciones). 
    // ENTONCES NO HACEMOS NADA.
    if (window.innerWidth === this.#lastWindowWidth) return;

    // Si el ancho cambió de verdad (rotación o PC), actualizamos y recalculamos
    this.#lastWindowWidth = window.innerWidth;

    const marqueeItems = this.#getMarqueeItems();
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = marqueeItems.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    
    // Reiniciar animación solo si hubo cambio real de ancho
    this.#restartAnimation();
  }, 250);

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 1; 
    const marqueeItems = this.#getMarqueeItems();
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;
    const count = marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
    
    return Math.sqrt(count) * speedFactor;
  }

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();
    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();
    const clone = this.refs.content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const marqueeItems = this.#getMarqueeItems();
    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const marqueeItems = this.#getMarqueeItems();
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;
    return marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }

  // Funciones de hover (slow down / speed up)
  #slowDown = debounce(() => {
    if (this.#animation) return;
    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1, to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => { this.#animation = null; },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();
    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;
    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();
    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from, to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => { this.#animation = null; },
    });
  }
}

// Función auxiliar de animación
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;
  function animate(currentTime) {
    if (cancelled) return;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;
    onUpdate(currentValue);
    if (progress < 1) requestAnimationFrame(animate);
    else if (typeof onComplete === 'function') onComplete();
  }
  requestAnimationFrame(animate);
  return { get current() { return currentValue; }, cancel() { cancelled = true; } };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}